<script setup lang="ts">
import { computed, inject, useId } from 'vue'
import MemberNameField from './MemberNameField.vue'
import { memberSuggestions, referencedNode } from './member-names'
import type { AggregateServiceModel } from '@/types'
import { BBEL_SCOPE_KEY, type BbelScopeContext } from '@/views/builder/composables/bbel-context'
import { EDITED_SERVICE_KEY, type EditedServiceContext } from '@/views/builder/composables/edited-service-context'

const props = defineProps<{
  modelValue: AggregateServiceModel
}>()

const emit = defineEmits<{
  'update:modelValue': [value: AggregateServiceModel]
}>()

function update(changes: Partial<AggregateServiceModel>) {
  emit('update:modelValue', { ...props.modelValue, ...changes })
}

const OPERATIONS: AggregateServiceModel['operation'][] = ['Sum', 'Count', 'Min', 'Max', 'Avg']

// Path names where the number sits inside one transaction's output of the service before this one, so its
// suggestions are the fields of that service's sampled output.
const scopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const editedService = inject<EditedServiceContext | undefined>(EDITED_SERVICE_KEY, undefined)
const pathSuggestions = computed(() => {
  const parentId = editedService?.value?.parentServiceIds[0]
  if (!parentId) return memberSuggestions(undefined, 'chains')
  const parentOutput = referencedNode(scopeCtx?.value.roots ?? [], { serviceId: parentId, projection: 'Output', path: [] })
  return memberSuggestions(parentOutput, 'chains')
})

const operationId = useId()
</script>

<template>
  <div>
    <div class="mb-3">
      <div class="form-label">Path</div>
      <MemberNameField
        label="Path"
        placeholder="The output is the number"
        :model-value="modelValue.path"
        :suggestions="pathSuggestions"
        @update:model-value="update({ path: $event })"
      />
      <div class="form-text">A value that is not a number stops the run.</div>
    </div>

    <div class="mb-3">
      <label class="form-label" :for="operationId">Operation</label>
      <select
        :id="operationId"
        class="form-select w-auto"
        :value="modelValue.operation"
        @change="update({ operation: ($event.target as HTMLSelectElement).value as AggregateServiceModel['operation'] })"
      >
        <option v-for="operation in OPERATIONS" :key="operation" :value="operation">{{ operation }}</option>
      </select>
    </div>
  </div>
</template>
